import React, { useRef } from 'react';

import { useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex } from '@atlaskit/primitives/components/flex';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Grid } from '@atlaskit/primitives/grid';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline } from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { ProductKeys } from '../../../common/constants/product-keys';
import { NewIssueNavigatorCTA } from '../../../common/ui/new-issue-navigator-cta';
import { Hide } from '../../../common/ui/width-observer';
import { shouldShowRefreshedFilterRow } from '../../../common/utils/experiments';
import { serveJiraNinFpsNavigation } from '../../../common/utils/experiments/experiment-exports';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import {
	useBootstrapLoading,
	useFeatures,
	usePrimaryProduct,
} from '../../../controllers/store/bootstrap';
import AppFilterInline from './app-filter-inline';
import { LoadingFilters } from './loading-filters';
import ProductFilters from './product-filters';
import { RefreshFilterRow } from './refresh-filter-row';
import { RefreshFilterRowSkeleton } from './refresh-filter-row/skeleton';

const filterRowStyles = xcss({
	overflow: 'hidden',
	position: 'relative',
	paddingBlock: 'space.050',
	paddingInline: 'space.050',
});

// In lite mode, the filters row needs to align flush with the More apps pill
// (no horizontal padding), so the More filters button starts at the same x.
const filterRowStylesLite = xcss({
	paddingInline: 'space.0',
});

const Filters = () => {
	const [bootstrapLoading] = useBootstrapLoading();
	const [features] = useFeatures();
	const filterContainerRef = useRef<HTMLDivElement>();
	const [primaryProduct] = usePrimaryProduct();
	const { locale } = useIntl();

	// Placed after every hook call so hook order stays stable. The refreshed row
	// replaces the legacy app-filter and product-filter rows wholesale, so it
	// bypasses the legacy skeleton below and shows its own while bootstrap loads.
	// Epic: https://product-fabric.atlassian.net/browse/QS-10718
	if (shouldShowRefreshedFilterRow(primaryProduct)) {
		if (bootstrapLoading) {
			return (
				<UFOLoadHold name="search-page-filters-loading">
					<RefreshFilterRowSkeleton />
				</UFOLoadHold>
			);
		}

		return (
			<UFOSegment name="search-page-refresh-filter-row">
				<RefreshFilterRow />
			</UFOSegment>
		);
	}

	if (bootstrapLoading) {
		return (
			<UFOLoadHold name="search-page-filters-loading">
				<LoadingFilters />
			</UFOLoadHold>
		);
	}

	const productFiltersWithClear = (
		<Inline space="space.100" ref={filterContainerRef}>
			<UFOSegment name="search-page-product-filters">
				<ProductFilters containerRef={filterContainerRef} />
			</UFOSegment>
		</Inline>
	);

	// In Lite mode, AppFilterInline is shown in the header row above filters — don't duplicate it here
	const isLiteMode = features?.lite?.enabled ?? false;

	// Side by side always gets the "responsive below sm" version
	if (features?.sideBySide?.enabled) {
		return (
			<Flex direction="column" gap="space.200">
				<UFOSegment name="search-page-sbs-app-filter-inline">
					<AppFilterInline />
				</UFOSegment>
				{productFiltersWithClear}
			</Flex>
		);
	}

	const jiraNinFpsNavigation = serveJiraNinFpsNavigation({ primaryProduct, locale });

	return (
		<Grid templateColumns="1fr auto" alignItems="center" gap="space.150">
			<Flex
				direction="column"
				gap="space.200"
				xcss={isLiteMode ? [filterRowStyles, filterRowStylesLite] : filterRowStyles}
			>
				{!isLiteMode && (
					<Hide target="layout" above="sm">
						<UFOSegment name="search-page-app-filter-inline">
							<AppFilterInline />
						</UFOSegment>
					</Hide>
				)}
				{productFiltersWithClear}
			</Flex>
			{(!jiraNinFpsNavigation || jiraNinFpsNavigation.cohort === 'control') &&
				primaryProduct === ProductKeys.Jira && <NewIssueNavigatorCTA />}
		</Grid>
	);
};

const FiltersWithErrorBoundary = (): React.JSX.Element => {
	return (
		<ErrorBoundary section="SearchPageFilters">
			<Filters />
		</ErrorBoundary>
	);
};

export default FiltersWithErrorBoundary;
